import React, { type ComponentType, memo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expValEquals, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	FAILURE,
	LOADING,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/constants.tsx';
import type {
	AvailableLayoutItemsSidebarProps,
	FieldGroupComponentProps as SidebarFieldGroupComponentProps,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import { WhatIsThis } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-category/what-is-this/index.tsx';
import { AvailableItemsList } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/available-items-list/index.tsx';
import { EmptyState as SidebarEmptyState } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/empty-state/index.tsx';
import { ErrorState as SidebarErrorState } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/error-state/index.tsx';
import { FieldGroup as SidebarFieldGroup } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/field-group/index.tsx';
import { FilterEfa as SidebarFilterEfa } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/filter-efa/index.tsx';
import { Filter as SidebarFilter } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/filter/index.tsx';
import { LoadingState as SidebarLoadingState } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/loading-state/index.tsx';
import { NotFoundState as SidebarNotFoundState } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/not-found-state/index.tsx';
import {
	PaddedColumnSection,
	SidebarDividingLine,
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	StateText as SidebarStateText,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/styled.tsx';
import { useAvailableLayoutItemGroups } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/utils.tsx';
import { Column } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/styled.tsx';
import { isGlobalAdmin } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/is-global-admin/index.tsx';
import { FieldTypes } from './field-types/index.tsx';
import messages, { fieldGroupMessages } from './messages.tsx';
import { RestrictedFieldGroup } from './restricted-field-group/index.tsx';
import { SidebarFooter } from './footer/index.tsx';

const FieldGroup = (props: SidebarFieldGroupComponentProps) => (
	<SidebarFieldGroup {...props} messages={fieldGroupMessages} />
);

const JsmTmpSidebarNew = memo<AvailableLayoutItemsSidebarProps>(
	(props: AvailableLayoutItemsSidebarProps) => {
		const { formatMessage } = useIntl();
		const {
			getDropAnimationWidth,
			itemGroups,
			filter,
			totalAvailableItemsCount,
			onFilterChange,
			onCreateField,
			isMouseDragging,
			onLoadMore,
			asyncState,
			availableFieldTypes,
			DroppableLayoutItemList,
			PanelComponent,
			FieldComponent,
			project,
			tenant,
			isDropDisabled,
			droppableListIdPrefix,
			getIndexAfterUnsortableFields,
			FieldTypeList,
			issueLayoutOwners,
			isFieldsLimitReached,
			fieldLimitPerProject,
			explicitFieldAssociation,
		} = props;
		const isJiraAdmin = isGlobalAdmin(tenant);

		const uiGroups = useAvailableLayoutItemGroups(filter, itemGroups);
		// there is always a single Issue type being an owner of the current layout
		const issueType = issueLayoutOwners[0].data;

		const fieldTypes = (
			<FieldTypes
				FieldTypeList={FieldTypeList}
				isFieldsLimitReached={isFieldsLimitReached}
				fieldLimitPerProject={fieldLimitPerProject}
				availableFieldTypes={availableFieldTypes}
				isMouseDragging={isMouseDragging}
				onFieldTypeClicked={onCreateField}
				getDropAnimationWidth={getDropAnimationWidth}
				issueTypeName={issueType.name}
			/>
		);

		const create = (
			<>
				<Box xcss={createSectionStyles}>
					<SidebarDividingLine />
				</Box>
				<PaddedColumnSection>{fieldTypes}</PaddedColumnSection>
			</>
		);

		const title = (
			<>
				<PaddedColumnSection>
					<Box xcss={titleWrapperStyles}>
						<Heading size="small" as="h2">
							{formatMessage(messages.title)}
						</Heading>
						<WhatIsThis
							content={formatMessage(messages.whatIsThisContent)}
							buttonText={formatMessage(messages.moreInformationAboutTheFieldsButtonText)}
						/>
					</Box>
				</PaddedColumnSection>
			</>
		);

		const isEmpty = !totalAvailableItemsCount && !uiGroups.length;
		const isFiltered = !!filter.name;

		const [expConfig] = UNSAFE_noExposureExp('efa_and_project_fields_page_for_tmp');
		const filterNode =
			explicitFieldAssociation.enabled && expConfig.get('isEnabled', false) ? (
				<SidebarFilterEfa
					onChange={onFilterChange}
					filter={filter}
					totalAvailableItemsCount={totalAvailableItemsCount}
					asyncState={asyncState}
				/>
			) : (
				<SidebarFilter
					onChange={onFilterChange}
					filter={filter}
					totalAvailableItemsCount={totalAvailableItemsCount}
					asyncState={asyncState}
				/>
			);

		if (asyncState === LOADING) {
			return (
				<>
					<Box xcss={scrollableSectionStyles}>
						<Column>
							{title}
							<Box xcss={noVerticalPaddingColumnStyles}>{filterNode}</Box>
							<SidebarLoadingState />
						</Column>
					</Box>
					{create}
					<SidebarFooter
						isGlobalAdmin={isJiraAdmin}
						isEfaEnabled={explicitFieldAssociation.enabled}
						project={project}
					/>
				</>
			);
		}

		if (asyncState === FAILURE) {
			return (
				<>
					<Box xcss={scrollableSectionStyles}>
						<Column>
							{title}
							<Box xcss={noVerticalPaddingColumnStyles}>{filterNode}</Box>
							<SidebarErrorState />
						</Column>
					</Box>
					{create}
					<SidebarFooter
						isGlobalAdmin={isJiraAdmin}
						isEfaEnabled={explicitFieldAssociation.enabled}
						project={project}
					/>
				</>
			);
		}

		if (isEmpty && !isFiltered) {
			return (
				<>
					<Box xcss={scrollableSectionStyles}>
						<Column>
							{title}
							<PaddedColumnSection>
								<SidebarEmptyState>
									<SidebarStateText>
										<Text weight="bold">{formatMessage(messages.emptyStateTitle)}</Text>
									</SidebarStateText>
									<SidebarStateText>
										{fg('itsm-issue-renaming')
											? formatMessage(messages.emptyStateMessageIssueTermRefresh)
											: formatMessage(messages.emptyStateMessage)}
									</SidebarStateText>
								</SidebarEmptyState>
							</PaddedColumnSection>
						</Column>
					</Box>
					{create}
					<SidebarFooter
						isGlobalAdmin={isJiraAdmin}
						isEfaEnabled={explicitFieldAssociation.enabled}
						project={project}
					/>
				</>
			);
		}

		if (isEmpty) {
			return (
				<>
					<Box xcss={scrollableSectionStyles}>
						<Column>
							{title}
							<Box xcss={noVerticalPaddingColumnStyles}>{filterNode}</Box>
							<SidebarNotFoundState>
								<SidebarStateText>
									<Text weight="bold">
										{expConfig.get('isEnabled', false) &&
											formatMessage(messages.notFoundStateTitleWithProjectName, {
												projectName: project.projectName,
											})}
										{!expConfig.get('isEnabled', false) &&
											formatMessage(messages.notFoundStateTitle)}
									</Text>
								</SidebarStateText>
								<SidebarStateText>
									{expConfig.get('isEnabled', false) &&
										formatMessage(messages.notFoundStateMessageWithFieldsPage)}
									{!expConfig.get('isEnabled', false) &&
										formatMessage(messages.notFoundStateMessage)}
								</SidebarStateText>
							</SidebarNotFoundState>
						</Column>
					</Box>
					{create}
					<SidebarFooter
						isGlobalAdmin={isJiraAdmin}
						isEfaEnabled={explicitFieldAssociation.enabled}
						project={project}
					/>
				</>
			);
		}

		return (
			<>
				<Box xcss={scrollableSectionStyles}>
					<Column>
						{title}
						<Box xcss={noVerticalPaddingColumnStyles}>{filterNode}</Box>
						<PaddedAvailableItems>
							<AvailableItemsList
								filter={filter}
								tenant={tenant}
								isDropDisabled={isDropDisabled}
								isRestrictedFieldsGroupVisible
								getIndexAfterUnsortableFields={getIndexAfterUnsortableFields}
								droppableListIdPrefix={droppableListIdPrefix}
								getDropAnimationWidth={getDropAnimationWidth}
								project={project}
								FieldComponent={FieldComponent}
								FieldGroupComponent={FieldGroup}
								PanelComponent={PanelComponent}
								DroppableLayoutItemList={DroppableLayoutItemList}
								RestrictedFieldGroupComponent={RestrictedFieldGroup}
								onLoadMore={onLoadMore}
								uiGroups={uiGroups}
							/>
						</PaddedAvailableItems>
					</Column>
				</Box>
				{create}
				<SidebarFooter
					isGlobalAdmin={isJiraAdmin}
					isEfaEnabled={explicitFieldAssociation.enabled}
					project={project}
				/>
			</>
		);
	},
);

const JsmTmpSidebarOld = memo<AvailableLayoutItemsSidebarProps>(
	(props: AvailableLayoutItemsSidebarProps) => {
		const { formatMessage } = useIntl();
		const {
			getDropAnimationWidth,
			itemGroups,
			filter,
			totalAvailableItemsCount,
			onFilterChange,
			onCreateField,
			isMouseDragging,
			onLoadMore,
			asyncState,
			availableFieldTypes,
			DroppableLayoutItemList,
			PanelComponent,
			FieldComponent,
			project,
			tenant,
			isDropDisabled,
			droppableListIdPrefix,
			getIndexAfterUnsortableFields,
			FieldTypeList,
			issueLayoutOwners,
			isFieldsLimitReached,
			fieldLimitPerProject,
			explicitFieldAssociation,
		} = props;

		const uiGroups = useAvailableLayoutItemGroups(filter, itemGroups);
		// there is always a single Issue type being an owner of the current layout
		const issueType = issueLayoutOwners[0].data;

		const title = (
			<>
				<PaddedColumnSection>
					<TitleWrapperOld>
						<Heading size="small" as="h2">
							{formatMessage(messages.title)}
						</Heading>
						<WhatIsThis
							content={formatMessage(messages.whatIsThisContent)}
							buttonText={formatMessage(messages.moreInformationAboutTheFieldsButtonText)}
						/>
					</TitleWrapperOld>
					<FieldTypes
						FieldTypeList={FieldTypeList}
						isFieldsLimitReached={isFieldsLimitReached}
						fieldLimitPerProject={fieldLimitPerProject}
						availableFieldTypes={availableFieldTypes}
						isMouseDragging={isMouseDragging}
						onFieldTypeClicked={onCreateField}
						getDropAnimationWidth={getDropAnimationWidth}
						issueTypeName={issueType.name}
					/>
				</PaddedColumnSection>
				<SidebarDividingLine />
			</>
		);

		const isEmpty = !totalAvailableItemsCount && !uiGroups.length;
		const isFiltered = !!filter.name;

		const [expConfig] = UNSAFE_noExposureExp('efa_and_project_fields_page_for_tmp');
		const filterNode =
			explicitFieldAssociation.enabled && expConfig.get('isEnabled', false) ? (
				<SidebarFilterEfa
					onChange={onFilterChange}
					filter={filter}
					totalAvailableItemsCount={totalAvailableItemsCount}
					asyncState={asyncState}
				/>
			) : (
				<SidebarFilter
					onChange={onFilterChange}
					filter={filter}
					totalAvailableItemsCount={totalAvailableItemsCount}
					asyncState={asyncState}
				/>
			);

		if (asyncState === LOADING) {
			return (
				<Column>
					{title}
					<PaddedColumnNoDividingLine>{filterNode}</PaddedColumnNoDividingLine>
					<SidebarLoadingState />
				</Column>
			);
		}

		if (asyncState === FAILURE) {
			return (
				<Column>
					{title}
					<PaddedColumnNoDividingLine>{filterNode}</PaddedColumnNoDividingLine>
					<SidebarErrorState />
				</Column>
			);
		}

		if (isEmpty && !isFiltered) {
			return (
				<Column>
					{title}
					<PaddedColumnSection>
						<SidebarEmptyState>
							<SidebarStateText>
								<Text weight="bold">{formatMessage(messages.emptyStateTitle)}</Text>
							</SidebarStateText>
							<SidebarStateText>
								{fg('itsm-issue-renaming')
									? formatMessage(messages.emptyStateMessageIssueTermRefresh)
									: formatMessage(messages.emptyStateMessage)}
							</SidebarStateText>
						</SidebarEmptyState>
					</PaddedColumnSection>
				</Column>
			);
		}

		if (isEmpty) {
			return (
				<Column>
					{title}
					<PaddedColumnNoDividingLine>{filterNode}</PaddedColumnNoDividingLine>
					<SidebarNotFoundState>
						<SidebarStateText>
							<Text weight="bold">{formatMessage(messages.notFoundStateTitle)}</Text>
						</SidebarStateText>
						<SidebarStateText>{formatMessage(messages.notFoundStateMessage)}</SidebarStateText>
					</SidebarNotFoundState>
				</Column>
			);
		}

		return (
			<Column>
				{title}
				<PaddedColumnNoDividingLine>{filterNode}</PaddedColumnNoDividingLine>
				<PaddedAvailableItems>
					<AvailableItemsList
						filter={filter}
						tenant={tenant}
						isDropDisabled={isDropDisabled}
						isRestrictedFieldsGroupVisible
						getIndexAfterUnsortableFields={getIndexAfterUnsortableFields}
						droppableListIdPrefix={droppableListIdPrefix}
						getDropAnimationWidth={getDropAnimationWidth}
						project={project}
						FieldComponent={FieldComponent}
						FieldGroupComponent={FieldGroup}
						PanelComponent={PanelComponent}
						DroppableLayoutItemList={DroppableLayoutItemList}
						RestrictedFieldGroupComponent={RestrictedFieldGroup}
						onLoadMore={onLoadMore}
						uiGroups={uiGroups}
					/>
				</PaddedAvailableItems>
			</Column>
		);
	},
);

export const JsmTmpSidebar = componentWithCondition(
	() => expValEquals('tmp_issue_type_edit_changes_reducing_duplicate_cfs', 'cohort', 'experiment'),
	JsmTmpSidebarNew,
	JsmTmpSidebarOld,
);

const createSectionStyles = xcss({
	paddingTop: 'space.200',
});

const titleWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	paddingTop: 'space.250',
	gap: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapperOld = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	paddingTop: token('space.250'),
	paddingBottom: token('space.100'),
	gap: token('space.100'),
});

const PaddedColumnNoDividingLine: ComponentType<{
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(Column)({
	paddingTop: token('space.150'),
	paddingRight: token('space.200'),
	paddingBottom: 0,
	paddingLeft: token('space.200'),
});

const PaddedAvailableItems: ComponentType<{
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(Column)({
	paddingTop: 0,
	paddingRight: token('space.200'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.200'),
});

const noVerticalPaddingColumnStyles = xcss({
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	display: 'flex',
	flexDirection: 'column',
});

const scrollableSectionStyles = xcss({
	overflow: 'auto',
	overflowWrap: 'break-word',
	flexGrow: 1,
	position: 'relative',
});
